import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import type { AccountWithStats } from '@olimhub/shared'
import { searchApi, groupsApi } from '@/api'
import { useToast } from '@/components/common/Toast'
import { Avatar } from '@/components/profile/Avatar'

interface InviteModalProps {
  groupSlug: string
  onClose: () => void
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function InviteModal({ groupSlug, onClose }: InviteModalProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<AccountWithStats[]>([])
  const [working, setWorking] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (query.trim().length < 2 || EMAIL_RE.test(query.trim())) { setResults([]); return }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await searchApi.searchAccounts(query.trim())
        setResults(data.items.slice(0, 6))
      } catch { setResults([]) }
    }, 250)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [query])

  async function inviteAccount(accountId: string) {
    setWorking(true)
    try {
      await groupsApi.invite(groupSlug, { accountId })
      toast.success(t('groups.invite_sent'))
      onClose()
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      toast.error(status === 409 ? t('groups.invite_already_member') : t('groups.invite_failed'))
    } finally {
      setWorking(false)
    }
  }

  async function inviteEmail() {
    setWorking(true)
    try {
      await groupsApi.invite(groupSlug, { email: query.trim() })
      toast.success(t('groups.invite_sent'))
      onClose()
    } catch {
      toast.error(t('groups.invite_failed'))
    } finally {
      setWorking(false)
    }
  }

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(8,10,15,0.52)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: 380, boxShadow: 'var(--shadow-modal)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ flex: 1, fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 'var(--fs-body)', color: 'var(--text-strong)' }}>
            {t('groups.invite_title')}
          </span>
          <button type="button" onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <X size={20} />
          </button>
        </div>
        <div style={{ padding: 16 }}>
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('groups.invite_placeholder')}
            style={{
              width: '100%',
              padding: '9px 12px',
              borderRadius: 'var(--radius-md)',
              border: '1.5px solid var(--border)',
              background: 'var(--bg-sunk)',
              color: 'var(--text-strong)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-body)',
              boxSizing: 'border-box',
              outline: 'none',
            }}
          />

          {results.length > 0 && (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {results.map((acc) => (
                <button
                  key={acc.id}
                  type="button"
                  disabled={working}
                  onClick={() => void inviteAccount(acc.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 6px', border: 'none', background: 'none', cursor: working ? 'not-allowed' : 'pointer', textAlign: 'start', borderRadius: 'var(--radius-sm)' }}
                >
                  <Avatar name={acc.displayName || acc.username} avatarUrl={acc.avatarUrl} animalAvatar={acc.animalAvatar} accountType={acc.accountType} size="sm" />
                  <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
                    {acc.displayName || acc.username}
                  </span>
                </button>
              ))}
            </div>
          )}

          {EMAIL_RE.test(query.trim()) && (
            <button
              type="button"
              disabled={working}
              onClick={() => void inviteEmail()}
              style={{ marginTop: 10, width: '100%', padding: '9px 0', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--brand)', color: 'var(--on-brand)', fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 'var(--fs-sm)', cursor: working ? 'not-allowed' : 'pointer' }}
            >
              {t('groups.invite_by_email', { email: query.trim() })}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
